import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import profileApi, { cachedProfileApi } from '../services/profileApi';

const ProfileContext = createContext();

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

export const ProfileProvider = ({ children }) => {
  const { user } = useAuth();

  // Loading and Error States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Franchise Profile State
  const [franchiseProfile, setFranchiseProfile] = useState({
    franchiseId: '',
    franchiseName: '',
    registrationNumber: '',
    gstNumber: '',
    establishedDate: '',
    businessType: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    contactDetails: {
      primaryPhone: '',
      secondaryPhone: '',
      email: '',
      website: ''
    },
    socialMedia: {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: ''
    },
    logo: null,
    coverImage: null
  });

  // Owner Details State
  const [ownerDetails, setOwnerDetails] = useState({
    fullName: '',
    dateOfBirth: '',
    gender: '',
    nationality: 'Indian',
    fatherName: '',
    motherName: '',
    maritalStatus: '',
    spouseName: '',
    permanentAddress: {
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    currentAddress: {
      sameAsPermanent: false,
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    contactInfo: {
      mobile: '',
      alternateMobile: '',
      email: '',
      emergencyContact: {
        name: '',
        relation: '',
        phone: ''
      }
    },
    profilePhoto: null
  });

  // KYC Documents State
  const [kycDocuments, setKycDocuments] = useState({
    aadhar: {
      number: '',
      frontImage: null,
      backImage: null,
      verified: false,
      verificationDate: null
    },
    pan: {
      number: '',
      image: null,
      verified: false,
      verificationDate: null
    },
    passport: {
      number: '',
      frontImage: null,
      backImage: null,
      expiryDate: '',
      verified: false
    },
    drivingLicense: {
      number: '',
      frontImage: null,
      backImage: null,
      expiryDate: '',
      verified: false
    },
    voterCard: {
      number: '',
      image: null,
      verified: false
    },
    businessRegistration: {
      documentType: '',
      documentNumber: '',
      documentImage: null,
      verified: false
    },
    gstCertificate: {
      image: null,
      verified: false
    },
    tradeLicense: {
      number: '',
      image: null,
      expiryDate: '',
      verified: false
    },
    verificationStatus: 'pending'
  });

  // Bank Accounts State
  const [bankAccounts, setBankAccounts] = useState([]);
  const [primaryAccountId, setPrimaryAccountId] = useState(null);

  // Metadata State
  const [metadata, setMetadata] = useState({});

  // Load Profile Data from API
  const loadProfileData = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    try {
      const response = await cachedProfileApi.getProfile(forceRefresh);

      if (response.status && response.data) {
        const {
          franchiseProfile: franchise,
          ownerDetails: owner,
          kycDocuments: kyc,
          bankAccounts: accounts,
          metadata: meta
        } = response.data;

        // Update all states with API data
        if (franchise) setFranchiseProfile(franchise);
        if (owner) setOwnerDetails(owner);
        if (kyc) setKycDocuments(kyc);
        if (accounts) {
          setBankAccounts(accounts);
          const primaryAcc = accounts.find(acc => acc.isPrimary);
          if (primaryAcc) setPrimaryAccountId(primaryAcc.id);
        }
        if (meta) setMetadata(meta);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Failed to load profile data. Please try again.');

      // Load from localStorage as fallback
      loadFromLocalStorage();
    } finally {
      setLoading(false);
    }
  }, []);

  // Save data to localStorage
  const saveToLocalStorage = useCallback(() => {
    try {
      localStorage.setItem('franchiseProfile', JSON.stringify(franchiseProfile));
      localStorage.setItem('ownerDetails', JSON.stringify(ownerDetails));
      localStorage.setItem('kycDocuments', JSON.stringify(kycDocuments));
      localStorage.setItem('bankAccounts', JSON.stringify(bankAccounts));
      localStorage.setItem('primaryAccountId', primaryAccountId || '');
    } catch (err) {
      console.error('Error saving to localStorage:', err);
    }
  }, [franchiseProfile, ownerDetails, kycDocuments, bankAccounts, primaryAccountId]);

  // Load from localStorage
  const loadFromLocalStorage = useCallback(() => {
    try {
      const savedProfile = localStorage.getItem('franchiseProfile');
      const savedOwner = localStorage.getItem('ownerDetails');
      const savedKyc = localStorage.getItem('kycDocuments');
      const savedAccounts = localStorage.getItem('bankAccounts');
      const savedPrimaryId = localStorage.getItem('primaryAccountId');

      if (savedProfile) setFranchiseProfile(JSON.parse(savedProfile));
      if (savedOwner) setOwnerDetails(JSON.parse(savedOwner));
      if (savedKyc) setKycDocuments(JSON.parse(savedKyc));
      if (savedAccounts) setBankAccounts(JSON.parse(savedAccounts));
      if (savedPrimaryId) setPrimaryAccountId(savedPrimaryId);
    } catch (err) {
      console.error('Error loading from localStorage:', err);
    }
  }, []);

  // Update Franchise Profile with API
  const updateFranchiseProfile = async (data) => {
    setSaving(true);
    try {
      const response = await profileApi.updateFranchiseProfile(data);
      if (response.status) {
        setFranchiseProfile(data);
        cachedProfileApi.clearCache();
        saveToLocalStorage();
      }
      return response;
    } catch (err) {
      console.error('Error updating franchise profile:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // Update Owner Details with API
  const updateOwnerDetails = async (data) => {
    setSaving(true);
    try {
      const response = await profileApi.updateOwnerDetails(data);
      if (response.status) {
        setOwnerDetails(data);
        cachedProfileApi.clearCache();
        saveToLocalStorage();
      }
      return response;
    } catch (err) {
      console.error('Error updating owner details:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // Add Bank Account with API
  const addBankAccount = async (account) => {
    setSaving(true);
    try {
      const response = await profileApi.addBankAccount(account);
      if (response.status && response.data) {
        const newAccount = response.data;
        setBankAccounts(prev => [...prev, newAccount]);

        if (bankAccounts.length === 0) {
          setPrimaryAccountId(newAccount.id);
        }

        cachedProfileApi.clearCache();
        saveToLocalStorage();
        return newAccount;
      }
    } catch (err) {
      console.error('Error adding bank account:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // Remove Bank Account with API
  const removeBankAccount = async (accountId) => {
    setSaving(true);
    try {
      const response = await profileApi.deleteBankAccount(accountId);
      if (response.status) {
        setBankAccounts(prev => prev.filter(acc => acc.id !== accountId));

        if (primaryAccountId === accountId) {
          const remaining = bankAccounts.filter(acc => acc.id !== accountId);
          setPrimaryAccountId(remaining.length > 0 ? remaining[0].id : null);
        }

        cachedProfileApi.clearCache();
        saveToLocalStorage();
      }
    } catch (err) {
      console.error('Error removing bank account:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // Set Primary Account with API
  const setPrimaryAccount = async (accountId) => {
    setSaving(true);
    try {
      const response = await profileApi.setPrimaryBankAccount(accountId);
      if (response.status) {
        setBankAccounts(prev =>
          prev.map(acc => ({
            ...acc,
            isPrimary: acc.id === accountId
          }))
        );
        setPrimaryAccountId(accountId);
        cachedProfileApi.clearCache();
        saveToLocalStorage();
      }
    } catch (err) {
      console.error('Error setting primary account:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // Update Bank Account with API
  const updateBankAccount = async (accountId, updates) => {
    setSaving(true);
    try {
      const response = await profileApi.updateBankAccount(accountId, updates);
      if (response.status) {
        setBankAccounts(prev =>
          prev.map(acc =>
            acc.id === accountId ? { ...acc, ...updates } : acc
          )
        );
        cachedProfileApi.clearCache();
        saveToLocalStorage();
      }
    } catch (err) {
      console.error('Error updating bank account:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // Upload Document with API
  const uploadDocument = async (documentType, file, field) => {
    try {
      const response = await profileApi.uploadKycDocument(documentType, field, file);
      if (response.status && response.data) {
        // Update KYC documents state
        setKycDocuments(prev => ({
          ...prev,
          [documentType]: {
            ...prev[documentType],
            [field]: response.data.url,
            uploadedAt: response.data.uploadedAt
          }
        }));
        cachedProfileApi.clearCache();
        saveToLocalStorage();
        return response.data;
      }
    } catch (err) {
      console.error('Error uploading document:', err);
      throw err;
    }
  };

  // Verify KYC Document with API
  const verifyKycDocument = async (documentType, documentNumber) => {
    try {
      const response = await profileApi.verifyKycDocument(documentType, documentNumber);
      if (response.status) {
        setKycDocuments(prev => ({
          ...prev,
          [documentType]: {
            ...prev[documentType],
            verified: true,
            verificationDate: new Date().toISOString()
          }
        }));
        cachedProfileApi.clearCache();
        saveToLocalStorage();
      }
      return response;
    } catch (err) {
      console.error('Error verifying document:', err);
      throw err;
    }
  };

  // Calculate Profile Completion
  const calculateProfileCompletion = useCallback(() => {
    const sections = {
      franchise: 0,
      owner: 0,
      kyc: 0,
      bank: 0
    };

    // Franchise Profile Completion
    const franchiseFields = [
      franchiseProfile.franchiseName,
      franchiseProfile.registrationNumber,
      franchiseProfile.gstNumber,
      franchiseProfile.address.street,
      franchiseProfile.contactDetails.primaryPhone,
      franchiseProfile.contactDetails.email
    ];
    sections.franchise = (franchiseFields.filter(f => f).length / franchiseFields.length) * 100;

    // Owner Details Completion
    const ownerFields = [
      ownerDetails.fullName,
      ownerDetails.dateOfBirth,
      ownerDetails.permanentAddress.street,
      ownerDetails.contactInfo.mobile,
      ownerDetails.contactInfo.email
    ];
    sections.owner = (ownerFields.filter(f => f).length / ownerFields.length) * 100;

    // KYC Completion
    const kycFields = [
      kycDocuments.aadhar.number,
      kycDocuments.pan.number
    ];
    sections.kyc = (kycFields.filter(f => f).length / kycFields.length) * 100;

    // Bank Account
    sections.bank = bankAccounts.length > 0 ? 100 : 0;

    const overall = (sections.franchise + sections.owner + sections.kyc + sections.bank) / 4;

    return {
      overall: Math.round(overall),
      sections
    };
  }, [franchiseProfile, ownerDetails, kycDocuments, bankAccounts]);

  // Refresh profile data
  const refreshProfile = useCallback(() => {
    return loadProfileData(true);
  }, [loadProfileData]);

  // Load profile data on mount
  useEffect(() => {
    if (user) {
      loadProfileData();
    }
  }, [user, loadProfileData]);

  // Save to localStorage on changes
  useEffect(() => {
    if (!loading) {
      saveToLocalStorage();
    }
  }, [franchiseProfile, ownerDetails, kycDocuments, bankAccounts, primaryAccountId, loading, saveToLocalStorage]);

  const value = {
    // States
    loading,
    error,
    saving,
    franchiseProfile,
    ownerDetails,
    kycDocuments,
    bankAccounts,
    primaryAccountId,
    metadata,

    // Setters (for local updates)
    setFranchiseProfile,
    setOwnerDetails,
    setKycDocuments,
    setBankAccounts,

    // API Methods
    updateFranchiseProfile,
    updateOwnerDetails,
    addBankAccount,
    removeBankAccount,
    updateBankAccount,
    setPrimaryAccount,
    uploadDocument,
    verifyKycDocument,

    // Utilities
    calculateProfileCompletion,
    refreshProfile,
    loadProfileData
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};